import { injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { GlobalFilterController } from '@/features/global-filters/GlobalFilterController';
import { resolveDateRange } from '@/features/global-filters/GlobalFilterTypes';
import { globalFieldsForPage } from '@/features/global-filters/GlobalFilterScope';
import { LicensesApiService, type ConnectionNameEntry, type LicenseCostRecord, type LicensePool, type SeatCostEntryInput } from './LicensesApiService';
import { platformIdForProviderType, platformLabel } from '@/shared/ProviderPlatform';

/** All cost data for a single provider connection over the selected range. */
export interface ProviderCostGroup {
    connectionId: string;
    typeLabel: string;
    displayName: string;
    providerTypeId: string;
    total: number;
    currency: string;
    records: LicenseCostRecord[];
}

/** One bucket on the trend chart: a period plus per-connection amounts. */
export interface CostTrendPoint {
    period: string;
    [connectionId: string]: string | number;
}

/** A trend line: the connection it represents and its legend label. */
export interface CostTrendSeries {
    id: string;
    label: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const DAILY_BUCKET_MAX_DAYS = 45;

@injectable()
export class LicenseCostPageViewModel extends BaseViewModel {
    loading: boolean = false;
    loadError: string | null = null;

    providerCosts: ProviderCostGroup[] = [];
    trend: CostTrendPoint[] = [];
    trendSeries: CostTrendSeries[] = [];
    grandTotal: number = 0;
    currency: string = 'USD';

    connections: { id: string; label: string }[] = [];
    saving: boolean = false;
    saveError: string | null = null;
    saveSuccess: string | null = null;

    seatCostHistory: LicenseCostRecord[] = [];
    historyLoading: boolean = false;
    historyError: string | null = null;

    constructor(
        private readonly api: LicensesApiService,
        private readonly globalFilters: GlobalFilterController,
    ) {
        super();
    }

    /** Record an admin-entered seat/subscription cost, then reload so the tabs + trend reflect it. */
    async submitSeatCost(entry: SeatCostEntryInput): Promise<boolean> {
        this.saving = true;
        this.saveError = null;
        this.saveSuccess = null;
        this.notifyChange();
        try {
            const written = await this.api.recordSeatCosts([entry]);
            this.saveSuccess = `Recorded ${written} seat-cost record${written === 1 ? '' : 's'}.`;
            await Promise.all([this.load(), this.loadSeatCostHistory()]);
            return true;
        } catch (cause) {
            this.saveError = cause instanceof Error ? cause.message : 'Failed to record seat cost.';
            return false;
        } finally {
            this.saving = false;
            this.notifyChange();
        }
    }

    async initialize(): Promise<void> {
        await this.load();
    }

    /** Reloads cost data for the current global date range + provider filter. Driven by the view on global-filter change. */
    async reload(): Promise<void> {
        await this.load();
    }

    /** Load the last 12 months of admin-entered seat costs, independent of the selected range. */
    async loadSeatCostHistory(): Promise<void> {
        this.historyLoading = true;
        this.historyError = null;
        this.notifyChange();
        try {
            const end = new Date();
            const start = new Date(end.getTime());
            start.setMonth(start.getMonth() - 12);
            const records = await this.api.getCostRecords(start.toISOString(), end.toISOString());
            this.seatCostHistory = records
                .filter((record) => record.costSource === 'admin')
                .sort((a, b) => b.periodStart.localeCompare(a.periodStart));
        } catch (cause) {
            this.historyError = cause instanceof Error ? cause.message : 'Failed to load recorded seat costs.';
        } finally {
            this.historyLoading = false;
            this.notifyChange();
        }
    }

    private async load(): Promise<void> {
        this.loading = true;
        this.loadError = null;
        this.notifyChange();

        try {
            const { start, end } = resolveDateRange(this.globalFilters.dateRange);
            const scopedDims = this.globalFilters.dimensionsForFields(globalFieldsForPage('licenses'));
            const providerValues = scopedDims.find((filter) => filter.field === 'provider_connection_id')?.values ?? [];
            const [rawRecords, pools, connectionNames] = await Promise.all([
                this.api.getCostRecords(start.toISOString(), end.toISOString()),
                this.api.listPools(),
                this.loadConnectionNames(),
            ]);
            const records = providerValues.length > 0 ? rawRecords.filter((record) => providerValues.includes(record.providerConnectionId)) : rawRecords;

            const rangeDays = (end.getTime() - start.getTime()) / DAY_MS;
            this.providerCosts = buildProviderGroups(records, pools, connectionNames);
            this.grandTotal = this.providerCosts.reduce((sum, group) => sum + group.total, 0);
            this.currency = this.providerCosts[0]?.currency ?? records[0]?.currency ?? 'USD';
            this.trendSeries = this.providerCosts.map((group) => ({ id: group.connectionId, label: group.displayName }));
            this.trend = buildTrend(records, this.trendSeries, rangeDays <= DAILY_BUCKET_MAX_DAYS);
            this.connections = buildConnectionOptions(pools, connectionNames);
        } catch (cause) {
            this.loadError = cause instanceof Error ? cause.message : 'Failed to load cost data.';
        } finally {
            this.loading = false;
            this.notifyChange();
        }
    }

    private async loadConnectionNames(): Promise<Map<string, ConnectionNameEntry>> {
        try {
            return await this.api.getConnectionNames();
        } catch (cause) {
            console.debug('[LicenseCost] connection names unavailable', cause);
            return new Map();
        }
    }
}

interface ProviderMeta {
    typeLabel: string;
    displayName: string;
    providerTypeId: string;
}

function buildProviderGroups(
    records: LicenseCostRecord[],
    pools: LicensePool[],
    connectionNames: Map<string, ConnectionNameEntry>,
): ProviderCostGroup[] {
    const metaByConnection = resolveProviderMeta(pools, connectionNames);
    const groups = new Map<string, ProviderCostGroup>();

    for (const record of records) {
        const connectionId = record.providerConnectionId;
        const meta = metaByConnection.get(connectionId) ?? {
            typeLabel: connectionId,
            displayName: connectionId,
            providerTypeId: '',
        };
        let group = groups.get(connectionId);
        if (!group) {
            group = {
                connectionId,
                typeLabel: meta.typeLabel,
                displayName: meta.displayName,
                providerTypeId: meta.providerTypeId,
                total: 0,
                currency: record.currency,
                records: [],
            };
            groups.set(connectionId, group);
        }
        group.total += record.amount;
        group.records.push(record);
    }

    for (const group of groups.values()) {
        group.records.sort((a, b) => b.periodStart.localeCompare(a.periodStart));
    }

    return Array.from(groups.values()).sort((a, b) => b.total - a.total);
}

/** Display metadata per connection: seeded from license pools first, then from the
 * connections list for usage-only connections (e.g. AWS Bedrock) that have cost
 * records but no pool — without the second pass those groups fall back to raw ids. */
function resolveProviderMeta(
    pools: LicensePool[],
    connectionNames: Map<string, ConnectionNameEntry>,
): Map<string, ProviderMeta> {
    const meta = new Map<string, ProviderMeta>();
    for (const pool of pools) {
        const platformId = platformIdForProviderType(pool.providerTypeId);
        const typeLabel = platformId !== null ? platformLabel(platformId) : pool.providerDisplayName || pool.providerTypeId;
        const displayName = connectionNames.get(pool.providerConnectionId)?.displayName ?? (pool.providerDisplayName || typeLabel);
        meta.set(pool.providerConnectionId, { typeLabel, displayName, providerTypeId: pool.providerTypeId });
    }
    for (const [connectionId, entry] of connectionNames) {
        if (meta.has(connectionId)) continue;
        const platformId = platformIdForProviderType(entry.providerTypeId);
        const typeLabel = platformId !== null ? platformLabel(platformId) : entry.providerTypeId;
        meta.set(connectionId, { typeLabel, displayName: entry.displayName, providerTypeId: entry.providerTypeId });
    }
    return meta;
}

function buildConnectionOptions(pools: LicensePool[], connectionNames: Map<string, ConnectionNameEntry>): { id: string; label: string }[] {
    const meta = resolveProviderMeta(pools, connectionNames);
    const options: { id: string; label: string }[] = [];
    const seen = new Set<string>();
    for (const id of meta.keys()) {
        if (seen.has(id)) continue;
        seen.add(id);
        options.push({ id, label: meta.get(id)?.displayName ?? id });
    }
    return options;
}

function buildTrend(
    records: LicenseCostRecord[],
    series: CostTrendSeries[],
    bucketByDay: boolean,
): CostTrendPoint[] {
    const sliceEnd = bucketByDay ? 10 : 7;
    const byPeriod = new Map<string, Map<string, number>>();
    for (const record of records) {
        const period = record.periodStart.slice(0, sliceEnd);
        const amounts = byPeriod.get(period) ?? new Map<string, number>();
        amounts.set(record.providerConnectionId, (amounts.get(record.providerConnectionId) ?? 0) + record.amount);
        byPeriod.set(period, amounts);
    }

    return Array.from(byPeriod.keys())
        .sort((a, b) => a.localeCompare(b))
        .map((period) => {
            const amounts = byPeriod.get(period);
            const point: CostTrendPoint = { period };
            for (const s of series) {
                point[s.id] = amounts?.get(s.id) ?? 0;
            }
            return point;
        });
}
